/**
 * Rewrites one persisted zustand store's stored document in place (security-slice review round 2, Codex #5).
 *
 * The one-time rewrite that removes secret-bearing text an older build persisted must not pass the store's ordinary
 * persistence: its `partialize` caps (oversized SQL bodies become `query: ""` / `queryOmitted`) would delete unrelated
 * user content. This reads the document as it is stored, lets `transform` return the stored state with only the
 * fields it must change (the same object when nothing changes), and writes the result back — every other byte kept.
 * Never throws: when the document cannot be read or written, the in-memory state is already safe and the next
 * ordinary save writes the file.
 */
export function rewritePersistedState(name: string, transform: (state: Record<string, unknown>) => Record<string, unknown>): void {
  try {
    const storage = typeof localStorage === 'undefined' ? undefined : localStorage;
    const raw = storage?.getItem(name);
    if (!storage || !raw) return;
    const stored = JSON.parse(raw) as unknown;
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return;
    const document = stored as Record<string, unknown>;
    const state = document.state;
    if (!state || typeof state !== 'object' || Array.isArray(state)) return;
    const next = transform(state as Record<string, unknown>);
    if (next === state) return;
    storage.setItem(name, JSON.stringify({ ...document, state: next }));
  } catch {
    /* see the module comment */
  }
}

/** `items` mapped through `map` — the same array when no item changed; anything that is not an array as it is. */
export function mapIfArray<T>(items: T, map: (item: never) => unknown): T {
  if (!Array.isArray(items)) return items;
  const out = items.map((item) => map(item as never));
  return (out.every((item, index) => item === items[index]) ? items : out) as T;
}

/**
 * `items` without the entries that are not records (a damaged file: a string, a number, an array) — the same array
 * when every entry is one; anything else that is not an array (absent aside) becomes an empty one (review round 5, Fable
 * N5: an entry that cannot be read is not kept).
 */
export function keepRecords<T>(items: T): T {
  if (items === undefined || items === null) return items;
  if (!Array.isArray(items)) return [] as unknown as T;
  const kept = items.filter((item) => !!item && typeof item === 'object' && !Array.isArray(item));
  return (kept.length === items.length ? items : kept) as unknown as T;
}

/**
 * A list that must be one: its record entries (`keepRecords`), or an empty list for anything else, `null` and absent
 * included (review round 6, Codex R6-2: `keepRecords(null)` kept null and the caller's `.map` threw, aborting the
 * sanitization of every other entry).
 */
export function recordList<T>(items: unknown): T[] {
  return Array.isArray(items) ? keepRecords(items as T[]) : [];
}

